import {
  ProfileTemplatesApi,
  ProfileTemplatePublishStatus,
  type ProfileTemplate,
} from '@up2stay/up2promo-api-client';
import { useCallback, type MutableRefObject } from 'react';
import { toast } from 'sonner';
import { shellPaths } from '@/core/router/app-paths';

import { extractApiErrorMessage } from '@/core/lib/profile-template-editor-snapshot';
import { emitTemplateSyncStarted } from '@/core/lib/profile-template-sync-events';
import type { TemplateFormData } from '@/core/lib/profile-template-form';

type TemplateLifecycleDict = {
  publishSuccess: string;
  profilesSyncEnqueued: string;
  publishError: string;
  discardChangesSuccess: string;
  discardChangesNotRestorable: string;
  discardChangesError: string;
  deleteSuccess: string;
  errors: { deleteFailed: string };
};

type UseTemplateDetailLifecycleOptions = {
  workspaceId: string | undefined;
  templateId: string | undefined;
  canManage: boolean;
  pt: TemplateLifecycleDict;
  applyTemplateToForm: (template: ProfileTemplate) => void;
  goBack: (fallback: string) => void;
  runWithoutGuard: (fn: () => void) => void;
  setPublishing: (value: boolean) => void;
  setDiscardingChanges: (value: boolean) => void;
  setPublishStatus: (value: ProfileTemplatePublishStatus) => void;
  setLiveRevision: (value: number | null) => void;
  setRevisionHistoryKey: (updater: (key: number) => number) => void;
  setDeleteConfirmOpen: (value: boolean) => void;
  saveTimeoutRef: MutableRefObject<ReturnType<typeof setTimeout> | null>;
  pendingFormPatchRef: MutableRefObject<Partial<TemplateFormData>>;
  suppressNotFoundRef: MutableRefObject<boolean>;
};

export function useTemplateDetailLifecycle({
  workspaceId,
  templateId,
  canManage,
  pt,
  applyTemplateToForm,
  goBack,
  runWithoutGuard,
  setPublishing,
  setDiscardingChanges,
  setPublishStatus,
  setLiveRevision,
  setRevisionHistoryKey,
  setDeleteConfirmOpen,
  saveTimeoutRef,
  pendingFormPatchRef,
  suppressNotFoundRef,
}: UseTemplateDetailLifecycleOptions) {
  const handlePublish = useCallback(async () => {
    if (!workspaceId || !templateId || !canManage) return;
    setPublishing(true);
    try {
      emitTemplateSyncStarted({
        workspaceId,
        templateId,
        reason: 'publish',
      });
      const res = await ProfileTemplatesApi.publish({
        path: { workspaceId, templateId },
      });
      const published = res.data;
      if (published?.publishStatus) setPublishStatus(published.publishStatus);
      if (published?.revision) setLiveRevision(published.revision);
      setRevisionHistoryKey((key) => key + 1);
      toast.success(pt.publishSuccess);
      if (published?.publishStatus === ProfileTemplatePublishStatus.SYNCING) {
        toast.message(pt.profilesSyncEnqueued);
      }
    } catch (err) {
      console.error(err);
      toast.error(extractApiErrorMessage(err, pt.publishError));
    } finally {
      setPublishing(false);
    }
  }, [
    canManage,
    pt.profilesSyncEnqueued,
    pt.publishError,
    pt.publishSuccess,
    setLiveRevision,
    setPublishStatus,
    setPublishing,
    setRevisionHistoryKey,
    templateId,
    workspaceId,
  ]);

  const handleDiscardChanges = useCallback(async () => {
    if (!workspaceId || !templateId || !canManage) return;
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
      saveTimeoutRef.current = null;
    }
    pendingFormPatchRef.current = {};

    setDiscardingChanges(true);
    try {
      const res = await ProfileTemplatesApi.discardChanges({
        path: { workspaceId, templateId },
      });
      const restored = res.data;
      if (restored) {
        applyTemplateToForm(restored);
      }
      const next = restored?.publishStatus;
      if (next) setPublishStatus(next);
      toast.success(pt.discardChangesSuccess);
    } catch (err) {
      console.error(err);
      const fallback =
        err &&
        typeof err === 'object' &&
        'response' in err &&
        (err as { response?: { data?: { errors?: Record<string, unknown> } } })
          .response?.data?.errors?.publishedBaseline ===
          'publishedBaselineNotRestorable'
          ? pt.discardChangesNotRestorable
          : pt.discardChangesError;
      toast.error(extractApiErrorMessage(err, fallback));
    } finally {
      setDiscardingChanges(false);
    }
  }, [
    applyTemplateToForm,
    canManage,
    pendingFormPatchRef,
    pt.discardChangesError,
    pt.discardChangesNotRestorable,
    pt.discardChangesSuccess,
    saveTimeoutRef,
    setDiscardingChanges,
    setPublishStatus,
    templateId,
    workspaceId,
  ]);

  const handleBack = () => goBack(shellPaths.moreProfileTemplates);

  const handleDelete = async () => {
    if (!workspaceId || !templateId) return;
    try {
      suppressNotFoundRef.current = true;
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
        saveTimeoutRef.current = null;
      }
      pendingFormPatchRef.current = {};
      await ProfileTemplatesApi.remove({
        path: { workspaceId, templateId },
      });
      setDeleteConfirmOpen(false);
      toast.success(pt.deleteSuccess);
      runWithoutGuard(() => goBack(shellPaths.moreProfileTemplates));
    } catch (err) {
      suppressNotFoundRef.current = false;
      console.error(err);
      toast.error(pt.errors.deleteFailed);
    }
  };

  return {
    handlePublish,
    handleDiscardChanges,
    handleBack,
    handleDelete,
  };
}
